import { Dialog } from '@headlessui/react'
import { usePostHog } from '@posthog/react'
import Button from '../layout/Button'
import { useState } from 'react'
import Modal from '../layout/Modal'
import TextInput from '../forms/TextInput'
import { addAlert } from '../../slices/alert'
import { useAppDispatch } from '../../hooks'
import { register, sendVerificationEmail } from '../../slices/auth'
import {
  extractApiError,
  fieldError,
  isValid,
  validateEmail,
  validatePassword,
  validatePasswordConfirmation,
  validateRequired,
  PASSWORD_MIN_LENGTH,
} from '../../utils/validation'

type RegisterState = {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirm_password: string;
  agree_marketing: boolean;
  agree_tos: boolean;
};

type RegisterErrors = Record<
  "first_name" | "last_name" | "email" | "password" | "confirm_password" | "agree_tos",
  string | null
>;

const NO_ERRORS: RegisterErrors = {
  first_name: null,
  last_name: null,
  email: null,
  password: null,
  confirm_password: null,
  agree_tos: null,
};

function validateAll(state: RegisterState): RegisterErrors {
  return {
    first_name: validateRequired(state.first_name, "First name"),
    last_name: validateRequired(state.last_name, "Last name"),
    email: validateEmail(state.email),
    password: validatePassword(state.password),
    confirm_password: validatePasswordConfirmation(state.password, state.confirm_password),
    agree_tos: state.agree_tos
      ? null
      : "Please accept the Terms of Use and Privacy Policy to continue",
  };
}

export default function RegisterModal({ show, setShow }: { show: boolean, setShow: (show: boolean) => void }) {

  const dispatch = useAppDispatch();
  const posthog = usePostHog();

  // Component level state
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [errors, setErrors] = useState<RegisterErrors>(NO_ERRORS);
  const [registerState, setRegisterState] = useState<RegisterState>({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    agree_marketing: false,
    agree_tos: false,
  });

  // Functions

  // Errors clear as the user types and reappear on blur, so a half-typed email is
  // not scolded mid-keystroke.
  function updateField(field: keyof RegisterState, value: string | boolean) {
    setRegisterState((previous) => ({ ...previous, [field]: value }));
    if (field in errors) {
      setErrors((previous) => ({ ...previous, [field]: null }));
    }
  }

  function validateFieldOnBlur(field: keyof RegisterErrors) {
    const allErrors = validateAll(registerState);
    setErrors((previous) => ({ ...previous, [field]: allErrors[field] }));
  }

  async function triggerRegister(e: any) {
    e.preventDefault()

    // Signup deliberately does not gate on cookie consent. It only relies on
    // strictly necessary cookies, which need no consent, and refusing service to
    // someone who declined tracking would make that consent non-free under GDPR.
    // Consent is collected by the CookieYes banner and can be revisited from the
    // "Cookie settings" link in the footer.

    const {
      first_name,
      last_name,
      email,
      password,
      confirm_password,
      agree_marketing,
      agree_tos
    } = registerState;

    // Validate the form
    const formErrors = validateAll(registerState);
    setErrors(formErrors);
    if (!isValid(formErrors)) {
      // Bucketed rather than the raw message: these are user-facing prose that
      // changes freely, and distinct strings would fragment the funnel.
      posthog.capture("signup_failed", { reason: "client_validation" })
      dispatch(addAlert("Please correct the highlighted fields", "orange"))
      return;
    }

    const res: any = await dispatch(register({
      firstName: first_name,
      lastName: last_name,
      email,
      username: email,
      currentPassword: password,
      confirmPassword: confirm_password,
      referralId: null,
      agreeTerms: agree_tos,
      agreeMarketing: agree_marketing,
    }))
    if (res.payload?.status === 200) {
      posthog.capture("signup_completed", { agreed_marketing: agree_marketing })
      dispatch(addAlert("Registration successful!", "mint-green"))
      setShowRegistrationSuccess(true)
      return;
    }

    // Surface what the server actually objected to. Rules the client cannot check
    // — a too-common password, an email already registered — only fail here, so
    // showing "Registration failed!" would leave the user with nothing to act on.
    setShowRegistrationSuccess(false)
    const data = res.payload?.data;
    if (data && typeof data === "object") {
      // The account is registered with the email as the username, so a "username"
      // error is really about the email field and belongs on that input.
      const emailError =
        fieldError(data, "email") ??
        (fieldError(data, "username") ? "This email already exists" : null);
      setErrors({
        ...NO_ERRORS,
        email: emailError,
        // Strength failures come back under "password", the repeat mismatch under
        // "confirm_password", and a missing password under "current_password".
        password: fieldError(data, "password") ?? fieldError(data, "current_password"),
        confirm_password: fieldError(data, "confirm_password"),
        agree_tos: fieldError(data, "agree_terms"),
      });
    }
    posthog.capture("signup_failed", {
      reason: "server_rejected",
      status: res.payload?.status ?? null,
    })
    dispatch(addAlert(extractApiError(res.payload, "Registration failed!"), "orange"))
  }

  return (
    <Modal
      show={show}
      setShow={setShow}
      confirmText="Register!"
      cancelText="Cancel"
      confirmHandler={() => { }}
      showActionButtons={false}
      showTriggerButton={false}
    >
      <div className="overflow-auto px-1">
        <div className="text-center">
          <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-black">
            {showRegistrationSuccess === false ? "Sign up with email" : "Verify your email"}
          </Dialog.Title>
          <div className="mt-10 grid grid-cols-1 gap-y-6">
            {showRegistrationSuccess === false ? (
              <>
                <p className="text-sm text-left text-black">These details will not be publicly visible</p>

                {/* First name */}
                <TextInput
                  id="register-first-name"
                  type="text"
                  label=""
                  borderClass="border border-black"
                  showLabel={false}
                  autoComplete="given-name"
                  onChange={(e) => updateField("first_name", e.target.value)}
                  onBlur={() => validateFieldOnBlur("first_name")}
                  error={errors.first_name}
                  placeholder="First name"
                  // small="Your first name will not be publicly visible"
                  value={registerState.first_name}
                />

                {/* Last name */}
                <TextInput
                  id="register-last-name"
                  type="text"
                  label=""
                  borderClass="border border-black"
                  showLabel={false}
                  autoComplete="family-name"
                  onChange={(e) => updateField("last_name", e.target.value)}
                  onBlur={() => validateFieldOnBlur("last_name")}
                  error={errors.last_name}
                  placeholder="Last name"
                  // small="Your last name will not be publicly visible"
                  value={registerState.last_name}
                />

                {/* Email */}
                <TextInput
                  id="register-email"
                  type="email"
                  label=""
                  borderClass="border border-black"
                  showLabel={false}
                  autoComplete="email"
                  onChange={(e) => updateField("email", e.target.value)}
                  onBlur={() => validateFieldOnBlur("email")}
                  error={errors.email}
                  placeholder="Email"
                  // small="Enter the email you want to use on RaiseDiversity"
                  value={registerState.email}
                />

                {/* Password */}
                <TextInput
                  id="register-password"
                  type="password"
                  label=""
                  borderClass="border border-black"
                  showLabel={false}
                  autoComplete="new-password"
                  onChange={(e) => updateField("password", e.target.value)}
                  onBlur={() => validateFieldOnBlur("password")}
                  error={errors.password}
                  placeholder="Password"
                  small={`Please use at least ${PASSWORD_MIN_LENGTH} characters. Avoid common passwords and don't use numbers alone.`}
                  value={registerState.password}
                />

                {/* Confirm password */}
                <TextInput
                  id="register-confirm-password"
                  type="password"
                  label=""
                  borderClass="border border-black"
                  showLabel={false}
                  autoComplete="new-password"
                  onChange={(e) => updateField("confirm_password", e.target.value)}
                  onBlur={() => validateFieldOnBlur("confirm_password")}
                  error={errors.confirm_password}
                  placeholder="Confirm password"
                  // small="Please re-enter your password"
                  value={registerState.confirm_password}
                />

                {/* Marketing */}
                <div className="flex items-center">
                  <input
                    id="register-marketing"
                    type="checkbox"
                    className="h-4 w-4 text-blue border-gray-300 rounded"
                    checked={registerState.agree_marketing}
                    onChange={(e) => updateField("agree_marketing", e.target.checked)}
                  />
                  <label htmlFor="register-marketing" className="ml-2 block text-sm text-black text-left">
                    Yes, I’d like to receive occasional updates, offers, and news from RaiseDiversity. You can unsubscribe at any time.
                  </label>
                </div>

                {/* Terms of service */}
                <div>
                  <div className="flex items-center">
                    <input
                      id="register-tos"
                      type="checkbox"
                      className="h-4 w-4 text-blue border-gray-300 rounded"
                      checked={registerState.agree_tos}
                      onChange={(e) => updateField("agree_tos", e.target.checked)}
                      aria-invalid={errors.agree_tos ? true : undefined}
                      aria-describedby={errors.agree_tos ? "register-tos-error" : undefined}
                    />
                    <label htmlFor="register-tos" className="ml-2 block text-sm text-black text-left">
                      By signing up, I agree to the <a href="/terms-of-service" className="text-dark-blue underline">Terms of Use</a> and <a href="/privacy-policy" className="text-dark-blue underline">Privacy Policy</a>.
                    </label>
                  </div>
                  {errors.agree_tos && (
                    <p id="register-tos-error" role="alert" className="text-xs text-left text-orange mt-2">
                      {errors.agree_tos}
                    </p>
                  )}
                </div>

                {/* Submit button. Always rendered — previously it only appeared once
                    the terms box was ticked, which left anyone who missed the box
                    staring at a form with no way to submit and no explanation. */}
                <Button
                  bgColour="dark-blue"
                  borderColour="dark-blue"
                  onClick={(e) => triggerRegister(e)}
                >
                  <span className="text-white">Continue</span>
                </Button>
              </>
            ) : (
              <>
                <p className="text-black">
                  Account activation sent to your email address: <strong>{registerState.email}</strong>
                </p>
                <p className='text-black'>Please check your email and click the link to continue.</p>

                <p className="text-black">Didn't get the email? <span className="text-dark-blue underline cursor-pointer" onClick={
                  () => {
                    dispatch(sendVerificationEmail({ email: registerState.email }))
                    dispatch(addAlert("Email re-sent!", "mint-green"))
                  }
                }>Resend</span></p>
              </>
            )}

          </div>
        </div>
      </div>
    </Modal>
  );
}